import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import { NavLink } from "react-router-dom";
import useAuth from "../../hooks/useAuth";

import { Drawer as MuiDrawer, ListItemButton, Typography } from "@mui/material";
import { Logout, Info } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";

import { ReactComponent as Logo } from "../../vendor/logo-white.svg";
import { SidebarItemsType } from "../../types/sidebar";
import SidebarNav from "./SidebarNav";
import { File } from "../../types/app";

import { mainResetAll, setLandingDialogState } from "../../redux/slices/main";
import { searchResetAll } from "../../redux/slices/search";
import { populateResetAll } from "../../redux/slices/populate";
import { bookmarkResetAll } from "../../redux/slices/bookmark";

import useAppSelector from "../../hooks/useAppSelector";
import useAppDispatch from "../../hooks/useAppDispatch";
import { RootState } from "../../redux/store";

const Drawer = styled(MuiDrawer)`
  border-right: 0;

  > div {
    border-right: 0;
  }
`;

const Brand = styled(ListItemButton)<{
  component?: React.ReactNode;
  to?: string;
}>`
  font-size: ${(props) => props.theme.typography.h5.fontSize};
  font-weight: ${(props) => props.theme.typography.fontWeightMedium};
  color: ${(props) => props.theme.sidebar.header.color};
  background-color: ${(props) => props.theme.sidebar.header.background};
  font-family: ${(props) => props.theme.typography.fontFamily};
  min-height: 56px;
  padding-left: ${(props) => props.theme.spacing(6)};
  padding-right: ${(props) => props.theme.spacing(6)};
  justify-content: center;
  cursor: pointer;
  flex-grow: 0;

  ${(props) => props.theme.breakpoints.up("sm")} {
    min-height: 64px;
  }

  &:hover {
    background-color: ${(props) => props.theme.sidebar.header.background};
  }
`;

const BrandIcon = styled(Logo)`
  margin-right: ${(props) => props.theme.spacing(2)};
  color: ${(props) => props.theme.sidebar.header.brand.color};
  fill: ${(props) => props.theme.sidebar.header.brand.color};
  width: 140px;
  height: 70px;
`;

const UtilContainer = styled.div`
  display: flex;
  justify-content: space-evenly;
  background-color: ${(props) => props.theme.sidebar.header.background};
`;

const ButtonContainer = styled(UtilContainer)`
  cursor: pointer;
  margin-bottom: 12px;
`;

const UtilText = styled(Typography)`
  padding-top: 1px;
  margin-left: 5px;
`;

const InfoIcon = styled(Info)<{ hover: string }>`
  color: ${(props) =>
    props.hover === "info"
      ? props.theme.sidebar.color
      : props.theme.header.color};
`;

const LogoutIcon = styled(Logout)<{ hover: string }>`
  color: ${(props) =>
    props.hover === "logout"
      ? props.theme.sidebar.color
      : props.theme.header.color};
`;

export type SidebarProps = {
  PaperProps: {
    style: {
      width: number;
    };
  };
  variant?: "permanent" | "persistent" | "temporary";
  open?: boolean;
  onClose?: () => void;
  items: {
    title: string;
    pages: SidebarItemsType[];
  }[];
  showFooter?: boolean;
};

const Sidebar: React.FC<SidebarProps> = ({
  items,
  showFooter = true,
  ...rest
}) => {
  const { signOutt } = useAuth();
  const files = useAppSelector((state: RootState) => state.main.files);
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const [updatedItems, setUpdatedItems] = useState(items);
  const [hover, setHover] = useState<string>("");

  const handleSignOut = () => {
    signOutt();
    dispatch(mainResetAll());
    dispatch(searchResetAll());
    dispatch(populateResetAll());
    dispatch(bookmarkResetAll());
  };

  useEffect(() => {
    const temp = [...updatedItems];
    if (files.length !== 0) {
      const fileRoutes = files.map((file: File) => {
        return {
          title: file.fileName,
          href: `/${file.fileName}`,
        };
      });
      temp[0].pages.find((page) => page.title === "Files")!.children =
        fileRoutes as SidebarItemsType[];
    } else {
      temp[0].pages.find((page) => page.title === "Files")!.children = [];
    }
    setUpdatedItems(temp);
  }, [files]);

  return (
    <Drawer variant="permanent" {...rest}>
      <Brand component={NavLink as any} to="/">
        <BrandIcon />
      </Brand>
      <SidebarNav items={updatedItems} />
      <UtilContainer>
        <ButtonContainer
          onMouseEnter={() => setHover("info")}
          onMouseLeave={() => setHover("")}
          onClick={() => dispatch(setLandingDialogState(true))}
        >
          <InfoIcon hover={hover} />
          <UtilText
            color={
              hover === "info"
                ? (theme as any).sidebar.color
                : (theme as any).header.color
            }
          >
            Info
          </UtilText>
        </ButtonContainer>
        <ButtonContainer
          onMouseEnter={() => setHover("logout")}
          onMouseLeave={() => setHover("")}
          onClick={handleSignOut}
        >
          <LogoutIcon hover={hover} />
          <UtilText
            color={
              hover === "logout"
                ? (theme as any).sidebar.color
                : (theme as any).header.color
            }
          >
            Logout
          </UtilText>
        </ButtonContainer>
      </UtilContainer>
    </Drawer>
  );
};

export default Sidebar;
